import { useEffect, useState } from 'react';
import areaApi from '../service/areaApi';
import { getRegionList } from '../service/regionApi';

interface AreaOption {
  value: any;
  label: string;
}

export interface UseAreaOptionsConfig {
  /** 为 true 时加载全部城市（不依赖省份），用于命令解析等按城市名匹配的场景 */
  loadAllCities?: boolean;
}

/**
 * 通用 Hook：地区选项的动态加载
 * - 大区：从 cos_region_cache 获取
 * - 省份/城市：从 areaApi 获取（支持根据大区/省份联动）
 * - loadAllCities=true 时额外加载全部城市，供「沈阳」等城市名匹配
 */
export const useAreaOptions = (
  formValues?: any,
  config?: UseAreaOptionsConfig
) => {
  const [regionOptions, setRegionOptions] = useState<AreaOption[]>([]);
  const [provinceOptions, setProvinceOptions] = useState<AreaOption[]>([]);
  const [cityOptions, setCityOptions] = useState<AreaOption[]>([]);
  const [loading, setLoading] = useState({
    regions: false,
    provinces: false,
    cities: false,
  });

  // 加载大区选项（从 cos_region_cache 表获取，只在首次加载时执行）
  useEffect(() => {
    let mounted = true;
    setLoading(prev => ({ ...prev, regions: true }));

    getRegionList({ pageNum: 1, pageSize: 1000, status: 0 })
      .then(response => {
        if (mounted && response.code === 1 && response.data?.list) {
          const validRegions = response.data.list.filter(
            region => region.id !== undefined && region.name !== undefined
          );
          const sortedRegions = [...validRegions].sort((a, b) => {
            // 先按 sort 排序，再按 id 排序
            if (a.sort !== undefined && b.sort !== undefined) {
              if (a.sort !== b.sort) return a.sort - b.sort;
            }
            return (a.id || 0) - (b.id || 0);
          });
          setRegionOptions(
            sortedRegions.map(region => ({
              value: region.id as number,
              label: region.name || `大区 ${region.id}`,
            }))
          );
        } else if (mounted) {
          setRegionOptions([]);
        }
      })
      .catch(() => {
        if (mounted) {
          setRegionOptions([]);
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(prev => ({ ...prev, regions: false }));
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  // 根据 regionId 加载省份选项
  const regionId = formValues?.regionId;
  useEffect(() => {
    let mounted = true;
    setLoading(prev => ({ ...prev, provinces: true }));

    const loadProvinces = async () => {
      try {
        let provinces;
        if (regionId) {
          // 有选择大区，加载该大区的省份
          const response = await areaApi.getAreaList({ level: 1, topId: regionId, status: 1 });
          provinces = response.data?.list || [];
        } else {
          // 没有选择大区，加载所有省份
          provinces = await areaApi.getProvinces();
        }

        if (mounted) {
          setProvinceOptions(
            provinces.map(province => ({
              value: province.id,
              label: province.name || `省份${province.id}`,
            }))
          );
        }
      } catch {
        if (mounted) {
          setProvinceOptions([]);
        }
      } finally {
        if (mounted) {
          setLoading(prev => ({ ...prev, provinces: false }));
        }
      }
    };

    loadProvinces();

    return () => {
      mounted = false;
    };
  }, [regionId]);

  // 根据 provinceId 加载城市选项（或 loadAllCities 时加载全部城市）
  const provinceId = formValues?.provinceId;
  const loadAllCities = config?.loadAllCities;

  useEffect(() => {
    let mounted = true;

    if (loadAllCities) {
      // 命令解析等场景：加载全部城市，便于「沈阳」等城市名匹配
      setLoading(prev => ({ ...prev, cities: true }));
      areaApi
        .getAllCities()
        .then(cities => {
          if (mounted) {
            setCityOptions(
              cities.map(city => ({
                value: city.id,
                label: city.name || `城市${city.id}`,
              }))
            );
          }
        })
        .catch(() => {
          if (mounted) setCityOptions([]);
        })
        .finally(() => {
          if (mounted) {
            setLoading(prev => ({ ...prev, cities: false }));
          }
        });
      return () => {
        mounted = false;
      };
    }

    if (!provinceId) {
      setCityOptions([]);
      return () => {
        mounted = false;
      };
    }

    setLoading(prev => ({ ...prev, cities: true }));

    areaApi.getCitiesByProvince(provinceId)
      .then(cities => {
        if (mounted) {
          setCityOptions(
            cities.map(city => ({
              value: city.id,
              label: city.name || `城市${city.id}`,
            }))
          );
        }
      })
      .catch(() => {
        if (mounted) {
          setCityOptions([]);
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(prev => ({ ...prev, cities: false }));
        }
      });

    return () => {
      mounted = false;
    };
  }, [provinceId, loadAllCities]);

  return {
    regionOptions,
    provinceOptions,
    cityOptions,
    loading,
  };
};
